import { useEffect } from "react";

/**
 * SEO Hook: sets document.title, meta description, canonical, OG tags
 * and optional BreadcrumbList JSON-LD per route.
 *
 * Usage in a page component (top of function body):
 *   useSeo({
 *     title: "Auto-Abo Köln · DNA Automotive",
 *     description: "...",
 *     path: "/auto-abo",
 *     breadcrumbs: [{ name: "Auto-Abo", url: "/auto-abo" }],
 *   });
 */
const BASE = "https://dna-automotive.de";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href, extra = {}) {
  if (!href) return;
  const selector = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    document.head.appendChild(s);
  }
  s.textContent = JSON.stringify(data);
}

export default function useSeo({ title, description, path = "/", image, breadcrumbs, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", `${BASE}${path}`, "property");
    if (image) setMeta("og:image", image.startsWith("http") ? image : `${BASE}${image}`, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (image) setMeta("twitter:image", image.startsWith("http") ? image : `${BASE}${image}`);
    setMeta("twitter:card", "summary_large_image");
    setLink("canonical", `${BASE}${path}`);

    if (breadcrumbs && breadcrumbs.length) {
      setJsonLd("breadcrumbs-jsonld", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Startseite", item: `${BASE}/` },
          ...breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: 2 + i,
            name: b.name,
            item: `${BASE}${b.url}`,
          })),
        ],
      });
    }
    if (jsonLd) setJsonLd("page-jsonld", jsonLd);
    return () => {
      const s = document.getElementById("breadcrumbs-jsonld");
      if (s) s.remove();
      const p = document.getElementById("page-jsonld");
      if (p) p.remove();
    };
  }, [title, description, path, image, JSON.stringify(breadcrumbs || []), JSON.stringify(jsonLd || null)]);
}
